import { Box, Card, TextField, Typography } from "@mui/material"
import { Columns2 } from "lucide-react"
import { BROCHURE_COLORS, BROCHURE_FONT, bulletRows, escapeHtml, splitLines, type UseCaseSection } from "./model"

// Editor for the two-column comparison (e.g. "What you need" vs "Where the old
// way falls short"). Each column's bullets are one-per-line text — simple to
// edit, balanced by design.
interface ComparisonSectionProps {
  section: UseCaseSection
  onUpdate: (sectionId: string, partial: any) => void
}

export function ComparisonSection({ section, onUpdate }: ComparisonSectionProps) {
  const c = section.content
  return (
    <Card sx={{ borderRadius: 0 }} style={{ marginBottom: 0 }}>
      <Box sx={{ p: 3 }}>
        <Typography component="span" sx={{ fontSize: "1.125rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Columns2 size={20} />
          Comparison
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Left column title</Typography>
            <TextField
              size="small"
              value={c.leftTitle || ""}
              onChange={(e) => onUpdate(section.id, { leftTitle: e.target.value })}
              placeholder="e.g., What an emergency demands"
            />
            <Typography component="label" sx={{ fontSize: 13, color: "text.secondary" }}>Bullets — one per line</Typography>
            <TextField
              multiline minRows={4} size="small"
              value={c.leftItems || ""}
              onChange={(e) => onUpdate(section.id, { leftItems: e.target.value })}
              placeholder={"Role-specific guidance for staff\nLocation-aware evacuation, in every language\nCommunication that persists beyond the building"}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Right column title</Typography>
            <TextField
              size="small"
              value={c.rightTitle || ""}
              onChange={(e) => onUpdate(section.id, { rightTitle: e.target.value })}
              placeholder="e.g., Where a legacy PBX falls short"
            />
            <Typography component="label" sx={{ fontSize: 13, color: "text.secondary" }}>Bullets — one per line</Typography>
            <TextField
              multiline minRows={4} size="small"
              value={c.rightItems || ""}
              onChange={(e) => onUpdate(section.id, { rightItems: e.target.value })}
              placeholder={"Endpoint-bound — only registered room phones\nNo mobility — the alert can't follow people\nVoice-only, one-shot — no text/mobile fallback"}
            />
          </Box>
        </Box>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}>
          Keep the two lists a similar length — the columns render balanced and stack on mobile.
        </Typography>
      </Box>
    </Card>
  )
}

function column(title: string, items: string[]): string {
  return `
                    <td class="mobile-stack" width="50%" valign="top" style="width: 50%; vertical-align: top; padding: 8px;">
                      <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" bgcolor="${BROCHURE_COLORS.soft}" style="background-color: ${BROCHURE_COLORS.soft}; border-radius: 12px;">
                        <tr>
                          <td style="padding: 22px 22px 14px 22px;">
                            <div style="width: 26px; height: 3px; background-color: ${BROCHURE_COLORS.lime}; border-radius: 2px; font-size: 0; line-height: 0;">&nbsp;</div>
                            <p style="margin: 10px 0 12px 0; color: ${BROCHURE_COLORS.navy}; font-family: ${BROCHURE_FONT}; font-size: 16px; font-weight: bold; line-height: 22px;">${escapeHtml(title)}</p>
                            <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">${bulletRows(items)}
                            </table>
                          </td>
                        </tr>
                      </table>
                    </td>`
}

// Serialize the comparison as two balanced, top-aligned columns of clean dash
// bullets. Stacks on mobile via `.mobile-stack`. Returns '' when empty.
export function comparisonToHtml(section: UseCaseSection): string {
  const c = section.content || {}
  const left = splitLines(c.leftItems)
  const right = splitLines(c.rightItems)
  if (left.length === 0 && right.length === 0) return ""

  return `
          <!-- Comparison -->
          <tr>
            <td bgcolor="${BROCHURE_COLORS.card}" class="mobile-pad" style="background-color: ${BROCHURE_COLORS.card}; padding: 8px 32px 36px 32px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
                <tr>
                  ${column(c.leftTitle || "", left)}
                  ${column(c.rightTitle || "", right)}
                </tr>
              </table>
            </td>
          </tr>
          `
}
